
import { supabase } from "@/integrations/supabase/client";
import { articleMediaService } from "./articleMediaService";
import { articleTagService } from "./articleTagService";
import { articleBlockService } from "./articleBlockService";
import { ArticleData } from "./types";

export const articleUpdateService = {
  async updateArticle(id: string, article: Partial<ArticleData>) {
    try {
      // Create a more straightforward approach without relying on edge functions
      const updateData: any = { ...article };
      delete updateData.tags; // Handle tags separately
      delete updateData.content; // Handle content separately via blocks
      
      // Remove read_time field which doesn't exist in the database and replace it with reading_time
      if (updateData.read_time !== undefined) {
        updateData.reading_time = updateData.read_time;
        delete updateData.read_time;
      }

      // Rename certain fields to match the database schema
      if (updateData.cover_image) {
        updateData.featured_image_url = updateData.cover_image;
        delete updateData.cover_image;
      }

      if (updateData.access_type) {
        updateData.payment_type = updateData.access_type;
        delete updateData.access_type;
      }

      if (updateData.price !== undefined) {
        updateData.micropayment_price = updateData.price;
        delete updateData.price;
      }

      // Validate critical fields
      if (updateData.title === '') {
        updateData.title = 'Untitled Article'; // Prevent empty titles
      }

      // Update the article metadata (not the content)
      const { data, error } = await supabase
        .from('articles')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw new Error(`Article metadata update failed: ${error.message}`);

      // If tags are provided, update them
      if (article.tags) {
        const tagResult = await articleTagService.updateArticleTags(id, article.tags);
        if (tagResult && tagResult.error) throw new Error(`Tags update failed: ${tagResult.error.message}`);
      }

      // Handle content update via blocks
      if (article.content) {
        // If content is a string, create a single paragraph block
        if (typeof article.content === 'string') {
          // Delete existing blocks with a more robust approach
          const deleteResult = await articleBlockService.deleteBlocksByArticleId(id);
          if (deleteResult && deleteResult.error) throw new Error(`Blocks deletion failed: ${deleteResult.error.message}`);
            
          // Create a new paragraph block
          const blockResult = await articleBlockService.createBlock({
            article_id: id,
            type: 'paragraph',
            content: { text: article.content },
            position: 0
          });
          
          if (blockResult && blockResult.error) throw new Error(`Block creation failed: ${blockResult.error.message}`);
        } 
        // If content is EditorJS format, update blocks
        else if (article.content.blocks) {
          const blocksResult = await articleBlockService.createBlocksFromEditorData(id, article.content);
          if (blocksResult && blocksResult.error) throw new Error(`Blocks update failed: ${blocksResult.error.message}`);
          
          // Process media from the blocks
          const { data: userData } = await supabase.auth.getUser();
          const authorId = userData.user?.id;
          if (authorId) {
            await articleMediaService.processArticleMedia(id, article.content.blocks, authorId);
          }
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error updating article:', error);
      return { data: null, error };
    }
  },

  async publishArticle(id: string) {
    try {
      // Use the database function to publish the article
      const { data, error } = await supabase
        .rpc('publish_article', { article_id: id });

      if (error) throw error;
      
      return { data, error: null };
    } catch (error) {
      console.error('Error publishing article:', error);
      return { data: null, error };
    }
  }
};
